import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorState, LoadingState } from '../../../shared/components/index.js';
import { resolvePeriodRange } from '../../../config/analytics-config.js';
import {
  aggregateInsights, campaignPerformanceFromInsights,
  fetchMetaAnalysis, fetchMetaSnapshot, fetchMetaStatus, selectInsightsForFilters
} from '../../../services/meta-api.js';
import MetaGrowthWorkbench from '../MetaGrowthWorkbench.jsx';
import MetaHierarchy from '../MetaHierarchy.jsx';
import MetaPeriodComparison from '../MetaPeriodComparison.jsx';
import './MarketingPage.css';

function marketingPeriodRange(key) {
  if (key === 'last_14_days') {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 13);
    const ymd = (d) => [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
    return { dateFrom: ymd(from), dateTo: ymd(today) };
  }
  return resolvePeriodRange(key);
}

const number = (value, digits = 0) => Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: digits, minimumFractionDigits: digits });
const money = (value, currency = 'CLP') => new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(value || 0));
const percent = (value) => number(value, 2) + '%';
const statusLabel = (value) => ({ ACTIVE: 'Activa', PAUSED: 'Pausada', WITH_RESULTS: 'Con actividad', CAMPAIGN_PAUSED: 'Pausada' })[value] || value || 'Sin estado';
function Metric({ label, value, hint }) {
  return <article className="marketing-metric"><p>{label}</p><strong>{value}</strong><span>{hint}</span></article>;
}
function AnaList({ items }) {
  return <ul>{(items || []).filter(Boolean).map((item, index) => <li key={index}>{item}</li>)}</ul>;
}
function downloadCsv(rows, currency, range) {
  const table = [
    ['Campaña', 'Inversión', 'Moneda', 'Leads Meta', 'CPL', 'CTR general', 'CPC general', 'Impresiones'],
    ...rows.map((row) => [row.name, row.metrics.spend, currency, row.metrics.leads, row.metrics.cpl, row.metrics.ctr, row.metrics.cpc, row.metrics.impressions])
  ];
  const csv = table.map((line) => line.map((item) => '"' + String(item ?? '').replace(/"/g, '""') + '"').join(',')).join('\n');
  const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'meta-ads-' + range.dateFrom + '-' + range.dateTo + '.csv';
  link.click();
  URL.revokeObjectURL(url);
}

/** Dashboard exclusivo de Meta Ads. Nunca consulta CRM, Kommo ni cohortes. */
export default function MarketingPage() {
  const defaults = useMemo(() => resolvePeriodRange('last_30_days'), []);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [period, setPeriod] = useState('last_30_days');
  const [campaign, setCampaign] = useState('all');
  const [adset, setAdset] = useState('all');
  const [ad, setAd] = useState('all');
  const [status, setStatus] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [question, setQuestion] = useState('');
  const loadRef = useRef(0);
  const analysisRef = useRef(0);

  const range = useMemo(() => ({ dateFrom, dateTo }), [dateFrom, dateTo]);
  const hasConnection = Boolean(status?.ads ? status.ads.ready : status?.configured && status?.tokenValid);
  const account = status?.adAccount?.data;
  const currency = account?.currency || 'CLP';
  const campaigns = snapshot?.campaigns || [];
  const adsets = snapshot?.adsets || [];
  const ads = snapshot?.ads || [];
  const byLevel = snapshot?.byLevel || { campaign: [], adset: [], ad: [] };
  const unavailableLevels = snapshot?.unavailableLevels || [];
  const hasEntityFilter = campaign !== 'all' || adset !== 'all' || ad !== 'all';

  const load = useCallback(async () => {
    const id = ++loadRef.current;
    analysisRef.current += 1;
    setLoading(true);
    setError('');
    setStatus(null);
    setSnapshot(null);
    setAnalysis(null);
    setAnalysisError('');
    try {
      const nextStatus = await fetchMetaStatus();
      if (id !== loadRef.current) return;
      setStatus(nextStatus);
      if (!(nextStatus?.ads ? nextStatus.ads.ready : nextStatus?.configured && nextStatus?.tokenValid)) return;
      const nextSnapshot = await fetchMetaSnapshot({ dateFrom, dateTo });
      if (id !== loadRef.current) return;
      setSnapshot(nextSnapshot);
      setCampaign('all');
      setAdset('all');
      setAd('all');
      setUpdatedAt(new Date());
    } catch (e) {
      if (id === loadRef.current) setError(e.message || 'No fue posible consultar Meta Ads.');
    } finally {
      if (id === loadRef.current) setLoading(false);
    }
  }, [dateFrom, dateTo, refreshKey]);

  useEffect(() => {
    void load();
    return () => { loadRef.current += 1; analysisRef.current += 1; };
  }, [load]);

  const visibleAdsets = useMemo(() => campaign === 'all' ? adsets : adsets.filter((x) => String(x.campaignId) === campaign), [adsets, campaign]);
  const visibleAds = useMemo(() => ads.filter((x) => (campaign === 'all' || String(x.campaignId) === campaign) && (adset === 'all' || String(x.adsetId) === adset)), [ads, campaign, adset]);
  const insights = useMemo(() => selectInsightsForFilters(byLevel, { campaign, adset, ad }), [byLevel, campaign, adset, ad]);
  const metrics = useMemo(() => aggregateInsights(insights), [insights]);
  const campaignPerformance = useMemo(() => campaignPerformanceFromInsights(campaigns, byLevel), [campaigns, byLevel]);
  const visibleCampaigns = useMemo(() => campaign === 'all' ? campaignPerformance : campaignPerformance.filter((x) => String(x.id) === campaign), [campaignPerformance, campaign]);

  const runAnalysis = useCallback(async (customQuestion = '') => {
    if (!hasConnection || loading || error) return;
    const id = ++analysisRef.current;
    setAnalysisLoading(true);
    setAnalysisError('');
    try {
      // ÚNICAMENTE Meta Ads: intentionally omit the commercial argument.
      const result = await fetchMetaAnalysis({ since: dateFrom, until: dateTo, question: customQuestion || undefined });
      if (id === analysisRef.current) setAnalysis(result);
    } catch (e) {
      if (id === analysisRef.current) setAnalysisError(e.message || 'No se pudo obtener el análisis de Meta.');
    } finally {
      if (id === analysisRef.current) setAnalysisLoading(false);
    }
  }, [hasConnection, loading, error, dateFrom, dateTo]);

  useEffect(() => {
    if (!loading && hasConnection && snapshot && !error) void runAnalysis();
  }, [loading, hasConnection, snapshot, error, runAnalysis]);

  const askAna = async (event) => {
    event.preventDefault();
    if (question.trim()) await runAnalysis(question.trim());
  };
  const ana = analysis?.ana;
  const findings = analysis?.deterministic?.findings || [];

  return <div className="analytics-page marketing-page">
    <header className="marketing-hero">
      <div>
        <p className="marketing-eyebrow">Meta Ads Intelligence · Datos exclusivos de Meta</p>
        <h1>Meta Ads</h1>
        <p>Analizá inversión, campañas, anuncios y resultados atribuidos por Meta, sin datos del CRM.</p>
        <div className="marketing-account-line">
          <span className={`marketing-live ${hasConnection ? 'is-on' : ''}`}>{hasConnection ? 'Conectado' : 'Sin conexión'}</span>
          <span>{account?.name || 'Cuenta publicitaria no identificada'}</span>
          {account?.accountId ? <span>#{account.accountId}</span> : null}
          <span>Solo lectura</span>
        </div>
      </div>
      <div className="marketing-hero-actions">
        <button type="button" className="analytics-btn" disabled={loading} onClick={() => setRefreshKey((n) => n + 1)}>{loading ? 'Actualizando…' : 'Actualizar datos'}</button>
        <button type="button" className="analytics-btn is-secondary" disabled={!visibleCampaigns.length} onClick={() => downloadCsv(visibleCampaigns, currency, range)}>Exportar CSV</button>
        <small>{updatedAt ? 'Actualizado ' + updatedAt.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : 'Esperando datos de Meta'}</small>
      </div>
    </header>

    <section className="marketing-filterbar" aria-label="Filtros de Meta Ads">
      <label><span>Período</span>
        <select value={period} onChange={(event) => {
          const selection = event.target.value;
          setPeriod(selection);
          if (selection !== 'custom') {
            const next = marketingPeriodRange(selection);
            setDateFrom(next.dateFrom);
            setDateTo(next.dateTo);
          }
        }}>
          <option value="today">Hoy</option>
          <option value="yesterday">Ayer</option>
          <option value="last_7_days">Últimos 7 días</option>
          <option value="last_14_days">Últimos 14 días</option>
          <option value="last_30_days">Últimos 30 días</option>
          <option value="current_month">Este mes</option>
          <option value="previous_month">Mes anterior</option>
          <option value="custom">Personalizado</option>
        </select>
      </label>
      <label><span>Desde</span><input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPeriod('custom'); }} /></label>
      <label><span>Hasta</span><input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPeriod('custom'); }} /></label>
      <label><span>Campaña</span><select value={campaign} onChange={(e) => { setCampaign(e.target.value); setAdset('all'); setAd('all'); }}><option value="all">Todas las campañas</option>{campaigns.map((x) => <option value={x.id} key={x.id}>{x.name}</option>)}</select></label>
      <label><span>Conjunto</span><select value={adset} disabled={unavailableLevels.includes('adset') || !adsets.length} onChange={(e) => { setAdset(e.target.value); setAd('all'); }}><option value="all">Todos los conjuntos</option>{visibleAdsets.map((x) => <option value={x.id} key={x.id}>{x.name}</option>)}</select></label>
      <label><span>Anuncio</span><select value={ad} disabled={unavailableLevels.includes('ad') || !ads.length} onChange={(e) => setAd(e.target.value)}><option value="all">Todos los anuncios</option>{visibleAds.map((x) => <option value={x.id} key={x.id}>{x.name}</option>)}</select></label>
    </section>

    {!loading && status && !hasConnection ? <div className="marketing-connection-error"><strong>Meta Ads no está disponible.</strong><span>Revisá la cuenta publicitaria y los permisos ads_read del token configurado en la API.</span></div> : null}
    {snapshot?.stale ? <div role="status" className="marketing-data-warning"><strong>Datos en caché</strong><span>Meta limitó las consultas; los valores pueden no estar actualizados.</span></div> : null}
    {unavailableLevels.length ? <div role="status" className="marketing-data-warning"><strong>Información parcial</strong><span>No se pudo consultar el desglose de {unavailableLevels.join(', ')}. No se muestran ceros inventados.</span></div> : null}
    {error ? <ErrorState message={error} onRetry={load} /> : null}
    {loading ? <LoadingState message="Consultando exclusivamente Meta Ads…" /> : null}

    {!loading && hasConnection && snapshot && !error ? <>
      <section className="marketing-section" id="marketing-rendimiento">
        <div className="marketing-section-head"><div><p>01 · Rendimiento</p><h2>Resultados de Meta Ads</h2></div><span>Fuente exclusiva: Meta Marketing API</span></div>
        {unavailableLevels.includes(ad !== 'all' ? 'ad' : adset !== 'all' ? 'adset' : 'campaign') ? <p>El detalle solicitado no está disponible por el momento.</p> : <div className="marketing-metrics-grid">
          <Metric label="Inversión" value={money(metrics.spend, currency)} hint="Gasto reportado por Meta" />
          <Metric label="Leads Meta" value={number(metrics.leads)} hint="Conversiones atribuidas por Meta" />
          <Metric label="Costo por lead" value={metrics.leads ? money(metrics.cpl, currency) : '—'} hint="Gasto dividido por leads Meta" />
          <Metric label="CTR general" value={percent(metrics.ctr)} hint="Todos los clics / impresiones" />
          <Metric label="CPC general" value={metrics.clicks ? money(metrics.cpc, currency) : '—'} hint="Costo por todos los clics" />
          <Metric label="Impresiones" value={number(metrics.impressions)} hint="Veces que se mostraron anuncios" />
          <Metric label="Alcance acumulado" value={number(metrics.reach)} hint="Suma de alcances por entidad, no usuarios únicos de la cuenta" />
          <Metric label="Campañas con inversión" value={number(visibleCampaigns.filter((x) => x.metrics.spend > 0).length)} hint="Con gasto registrado en el período" />
        </div>}
      </section>

      <section className="marketing-section" id="marketing-ana">
        <div className="marketing-section-head"><div><p>02 · Analista IA</p><h2>Ana analiza exclusivamente Meta</h2></div><span>{hasEntityFilter ? 'Análisis del total de la cuenta; los filtros no afectan el informe de IA' : 'Recomendaciones sin cambios automáticos'}</span></div>
        {analysisError ? <ErrorState message={analysisError} onRetry={() => runAnalysis()} /> : null}
        {analysisLoading ? <LoadingState message="Analizando campañas y rendimiento de Meta…" /> : null}
        {!analysisLoading && ana ? <div className="marketing-ana-layout">
          <article className={`marketing-ana-summary is-${ana.status || 'healthy'}`}><span>Estado del rendimiento</span><strong>{ana.status === 'critical' ? 'Crítico' : ana.status === 'attention' ? 'Atención' : 'Estable'}</strong><p>{ana.executiveSummary || 'No hay resumen disponible.'}</p><small>{analysis?.aiAvailable ? 'Análisis de IA' : 'Reglas objetivas'}</small></article>
          <div className="marketing-ana-columns">
            <article><h3>Qué funciona</h3><AnaList items={ana.whatWorks} /></article>
            <article><h3>Qué preocupa</h3><AnaList items={ana.concerns} /></article>
            <article><h3>Datos de respaldo</h3><AnaList items={findings.map((x) => x.title + ': ' + x.evidence)} /></article>
            <article><h3>Qué revisar</h3><AnaList items={ana.watchNext} /></article>
          </div>
          <div className="marketing-recommendations"><h3>Recomendaciones de campañas</h3>{(ana.recommendations || []).map((x, i) => <article key={i}><strong>{x.title}</strong><p>{x.why}</p></article>)}</div>
          <form className="marketing-ana-question" onSubmit={askAna}><label htmlFor="marketing-question">Preguntale a Ana sobre Meta Ads</label><div><input id="marketing-question" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="¿Qué anuncio optimizarías con estos resultados?" /><button className="analytics-btn" type="submit" disabled={analysisLoading || !question.trim()}>Analizar</button></div></form>
        </div> : null}
      </section>

      <section className="marketing-section" id="marketing-campanas">
        <div className="marketing-section-head"><div><p>03 · Campañas</p><h2>Inversión y resultados por campaña</h2></div><span>Datos atribuidos por Meta, sin cruces externos</span></div>
        <div className="marketing-table-wrap"><table className="marketing-table"><thead><tr><th>Campaña</th><th>Estado</th><th>Gasto</th><th>Leads Meta</th><th>CPL</th><th>CTR general</th><th>Impresiones</th></tr></thead><tbody>
          {visibleCampaigns.length ? visibleCampaigns.map((x) => <tr key={x.id}><td><strong>{x.name}</strong><small>{x.id}</small></td><td>{statusLabel(x.effectiveStatus || x.status)}</td><td>{money(x.metrics.spend, currency)}</td><td>{number(x.metrics.leads)}</td><td>{x.metrics.leads ? money(x.metrics.cpl, currency) : '—'}</td><td>{percent(x.metrics.ctr)}</td><td>{number(x.metrics.impressions)}</td></tr>) : <tr><td colSpan="7">No hay actividad publicitaria en este período.</td></tr>}
        </tbody></table></div>
      </section>

      <MetaPeriodComparison range={range} currency={currency} campaignInsights={byLevel.campaign} />

      <MetaHierarchy campaigns={campaigns} adsets={adsets} ads={ads} byLevel={byLevel} currency={currency} unavailableLevels={unavailableLevels} />

      <MetaGrowthWorkbench
        campaignPerformance={visibleCampaigns}
        adInsights={byLevel.ad}
        selectedInsights={insights}
        campaign={campaign}
        adset={adset}
        ad={ad}
        currency={currency}
        range={range}
        accountId={account?.accountId}
        unavailableAdLevel={unavailableLevels.includes('ad')}
      />
      <details className="marketing-methodology"><summary>Origen de la información</summary><p>La pantalla obtiene métricas exclusivamente de Meta Marketing API. Leads y costos por lead utilizan la atribución que informa Meta; no equivalen a clientes verificados, reuniones ni ventas.</p></details>
    </> : null}
  </div>;
}
